import CryptoJS from 'crypto-js';
import { StorageValue, PersistStorage } from 'zustand/middleware';

// Secret key for encryption - in production, this should be from environment variables
const SECRET_KEY = import.meta.env.VITE_APP_ZUSTAND_ENCRYPTION_KEY;

/**
 * Encrypts a string using AES encryption
 * @param text - The text to encrypt
 * @returns Encrypted string
 */
export const encrypt = (text: string): string => {
  try {
    const encrypted = CryptoJS.AES.encrypt(text, SECRET_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption failed:', error);
    return text; // Return original text if encryption fails
  }
};

/**
 * Decrypts an encrypted string
 * @param encryptedText - The encrypted text to decrypt
 * @returns Decrypted string
 */
export const decrypt = (encryptedText: string): string => {
  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedText, SECRET_KEY);
    const originalText = decrypted.toString(CryptoJS.enc.Utf8);

    // If decryption fails, it returns empty string
    if (!originalText) {
      console.warn('Decryption failed - returning original text');
      return encryptedText;
    }

    return originalText;
  } catch (error) {
    console.error('Decryption failed:', error);
    return encryptedText; // Return original text if decryption fails
  }
};

/**
 * Custom storage implementation with encryption for Zustand persist
 */
export const createEncryptedStorage = <T>(): PersistStorage<T> => ({
  getItem: (name: string): StorageValue<T> | null => {
    const item = localStorage.getItem(name);
    // Clear old encrypted user data if it exists
    localStorage.removeItem('user-data');
    if (!item) return null;

    try {
      // Try to decrypt the item
      const decrypted = decrypt(item);
      return JSON.parse(decrypted);
    } catch {
      // If decryption fails, try to parse as regular JSON (backward compatibility)
      try {
        return JSON.parse(item);
      } catch {
        return null;
      }
    }
  },

  setItem: (name: string, value: StorageValue<T>): void => {
    try {
      const serialized = JSON.stringify(value);
      const encrypted = encrypt(serialized);
      localStorage.setItem(name, encrypted);
    } catch (error) {
      console.error('Failed to encrypt and store item:', error);
      // Fallback to storing unencrypted
      try {
        localStorage.setItem(name, JSON.stringify(value));
      } catch {
        console.error('Failed to store item even without encryption');
      }
    }
  },

  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
});

/**
 * Simple encrypted storage for general use
 */
export const encryptedStorage = {
  getItem: (name: string): string | null => {
    const item = localStorage.getItem(name);
    if (!item) return null;

    try {
      // Try to decrypt the item
      const decrypted = decrypt(item);
      return decrypted;
    } catch {
      // If decryption fails, assume it's not encrypted (for backward compatibility)
      return item;
    }
  },

  setItem: (name: string, value: string): void => {
    try {
      const encrypted = encrypt(value);
      localStorage.setItem(name, encrypted);
    } catch (error) {
      console.error('Failed to encrypt and store item:', error);
      // Fallback to storing unencrypted
      localStorage.setItem(name, value);
    }
  },

  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

export function encryptPassword(password: string): string {
  const secretKey = import.meta.env.VITE_APP_SECRET_KEY;

  if (!secretKey) {
    throw new Error('VITE_APP_SECRET_KEY is not defined');
  }
  return CryptoJS.AES.encrypt(password, secretKey).toString();
}
